import { useState, useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import config from "config/config";
import { useTranslation } from "react-i18next";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuthorizationCheck } from "hooks/useAuthorizationCheck";
import { screenScrollTo, isCSR, isCSRAdmin, isFlagEnabled } from "js/utilities";
import Countdown from "react-countdown";
import { refreshToken } from "service/userservice/refreshToken";
import GeneralPurposeDialog from "components/General/GeneralPurposeDialog";
import SVGSprite from "components/General/SvgSprite";
import Header from "components/General/Header";
import PayeeHeader from "components/General/PayeeHeader";
import CsrSearchboard from "components/General/CSRSearchboard";
import AppAlertBanner from "components/General/AppAlertBanner";
import SessionRefresherModal from "components/ModalDialogs/SessionRefresherModal";
import UnauthHeader from "components/UnauthorizedUser/UnauthHeader";
import Breadcrumb from "components/General/Breadcrumb";
import Footer from "components/General/Footer";
import AuthenticatedUserChat from "components/Chat/AuthenticatedUserChat";
import UnauthenticatedUserChat from "components/Chat/UnauthenticatedUserChat";
import Toast from "components/General/Toast";
import Landing from "routes/Landing";
import Register from "routes/Register";
import ForgotPasswordContainer from "routes/forgot-password";
import ResetPasswordContainer from "routes/reset-password";
import ForgotUsername from "components/ForgotUsername/ForgotUsername";
import UnauthClaims from "routes/claims-unauth";
import Forms from "routes/Forms";
import CSRFormsPanel from "routes/Forms/CSRFormsPanel";
import Policies from "routes/policies";
import PolicyDetails from "routes/policy-details";
import Payment from "routes/payment";
import MakeAPayment from "routes/MakeAPayment";
import UnauthPayment from "routes/make-a-payment-unauth";
import AutoPay from "routes/autopay";
import ManagePayment from "routes/ManagePayment";
import ServicesClaims from "routes/services-claims";
import Services from "routes/Services";
import ServicesLifeInsurance from "routes/services-life-insurance";
import ServicesAnnuity from "routes/services-annuity";
import Claims from "routes/claims";
import FileAClaimAnnuity from "routes/file-a-claim-Annuity";
import FileAClaim from "routes/file-a-claim";
import ClaimsStatus from "routes/claims-status";
import UnauthFileAClaim from "routes/unauth-file-a-claim";
import Messages from "routes/messages";
import Help from "routes/Help";
import HelpFaqs from "routes/help-faqs";
import Billing from "routes/Billing";
import Profile from "routes/profile";
import ManageAutoPay from "routes/ManageAutoPay";
import TermsOfUse from "routes/terms-of-use";
import PrivacyPolicy from "routes/privacy-policy";
import CaliforniaConsumerPrivacyStatement from "routes/california-consumer-privacy-statement";
import AccessDenied from "routes/access-denied";
import CSRAdmin from "routes/CSRAdmin";
import EditPaymentFrequency from "routes/EditPaymentFrequency";
import Payee from "routes/Payee";
import { useVersionCheck } from "hooks/useVersionCheck";
import { ChatContext } from "context/Agent/ChatProvider";
import { getAdminAlerts } from "redux/slices/getAdminAlertsSlice";
import { convertToTitleCase } from "js/formatting";

export default function App() {
  useVersionCheck();
  const { csrUser } = useSelector((s) => s.csrUser);
  const { policies, claims } = useSelector((s) => s.data);
  const { t } = useTranslation();
  let navigate = useNavigate();
  let location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const { isLoading } = useSelector((state) => state.data);
  const [currentCounterTime, setCurrentCounterTime] = useState(null);
  const [countdownIdentifier, setCountdownIdentifier] = useState(0);
  const [sessionDialogState, setSessionDialogState] = useState(false);
  const [tokenErrorDialog, setTokenErrorDialog] = useState(false);
  const [bannerMessage, setBannerMessage] = useState();
  const [toastMessage, setToastMessage] = useState();
  const [bannerTitle, setBannerTitle] = useState();
  const [toasTitle, setToastTitle] = useState();

  // chat related hook
  const {
    hideCustomChat,
    setHideCustomChat,
    connectingToSF,
    setConnectingToSF,
    openChatWindow,
    setOpenChatWindow,
  } = useContext(ChatContext);

  useEffect(() => {
    screenScrollTo();

    // redirect user for login
    if (
      !user?.userId &&
      !isBasePathName() &&
      location.pathname.split("/")[1] !== "guest" &&
      location.pathname.split("/")[1] !== "help"
    ) {
      if (isCSR(csrUser) || isCSRAdmin(csrUser)) {
        if (
          !isFlagEnabled("ENABLE_CSR_ADMIN") &&
          !location.pathname === "/csradmin" &&
          !isCSRAdmin(csrUser)
        )
          navigate("/policies", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    }

    if (
      !isBasePathName() &&
      location.pathname.split("/")[1] !== "guest" &&
      location.pathname.split("/")[1] !== "help"
    ) {
      // Refersh Token
      refershToken();
    }

    if (user?.userId || isCSR(csrUser) || isCSRAdmin(csrUser)) {
      //set timer countdown to 1 hour and 55 mints
      setCurrentCounterTime(
        Date.now() + parseInt(config.SESSION_REFRESH_TIMEOUT)
      );
      setCountdownIdentifier(countdownIdentifier + 1);
    }
  }, [location.pathname]);
  const isBasePathName = () => {
    return [
      "/",
      "/forgot-password",
      "/forgot-username",
      "/register",
      "/reset-password",
      "/terms-of-use",
      "/privacy-policy",
      "/california-consumer-privacy-statement",
      "/survey",
    ].includes(location.pathname);
  };
  useEffect(() => {
    // if user or csr user not loggedin redirect to login
    // Refershing token throw 401 redirect to login
    if (
      !user?.userId &&
      !csrUser?.userId &&
      !isBasePathName() &&
      location.pathname.split("/")[1] !== "guest" &&
      location.pathname.split("/")[1] !== "help"
    )
      navigate("/", { replace: true });
    dispatch(getAdminAlerts()).then((response) => {
      if (response?.meta?.requestStatus === "fulfilled") {
        let customerToast = response?.payload?.activeAlerts?.filter(
          (a) =>
            (a.portal === "customer" || a.portal === "both") &&
            a.group === "toast"
        );
        let customerBanner = response?.payload?.activeAlerts?.filter(
          (a) =>
            (a.portal === "customer" || a.portal === "both") &&
            a.group === "banner"
        );
        let customerSortedBanner = customerBanner?.sort((a, b) =>
          a.id > b.id ? -1 : 1
        )[0];
        let customerSortedToast = customerToast?.sort((a, b) =>
          a.id > b.id ? -1 : 1
        )[0];
        setBannerMessage(customerSortedBanner?.message);
        setBannerTitle(convertToTitleCase(customerSortedBanner?.title));
        setToastMessage(customerSortedToast?.message);
        setToastTitle(convertToTitleCase(customerSortedToast?.title));
      }
    });
  }, [user, csrUser, location.pathname]);

  const refershToken = async () => {
    // Refershing token
    await refreshToken()
      .then((response) => {
        return response;
      })
      .catch((error) => {
        console.log(error);
        return false;
      });
  };

  const extendSession = () => {
    // Refersh Token
    refershToken();
    setSessionDialogState(false);
    //reset to countdown to 1 hour and 55 mints
    setCurrentCounterTime(
      Date.now() + parseInt(config.SESSION_REFRESH_TIMEOUT)
    );
    setCountdownIdentifier(countdownIdentifier + 1);
  };
  return (
    <div className="font-body text-theme-text-primary">
      <SVGSprite />
      <div className="flex flex-col min-h-screen">
        {csrUser?.userId && <CsrSearchboard portal="customer" />}
        {user?.userId && !isLoading ? (
          location.pathname === "/payee" ? (
            <PayeeHeader />
          ) : (
            <Header />
          )
        ) : !csrUser?.userId || isLoading ? (
          <UnauthHeader />
        ) : (
          ""
        )}
        <AppAlertBanner />
        <main id="mainContent" className="flex flex-col flex-grow">
          <div
            className={`border border-transparent ${
              config.COMPANY_NAME === "nttdata" &&
              window.location.pathname === "/"
                ? "flex flex-grow"
                : ""
            }`}
          >
            {toastMessage && (
              <Toast
                title={toasTitle ?? t("Toast-title")}
                message={toastMessage}
                isSticky={true}
              />
            )}

            {config.COMPANY_NAME === "nttdata" &&
            window.location.pathname === "/" ? (
              <></>
            ) : (
              <Breadcrumb />
            )}

            {isFlagEnabled("ENABLE_CUSTOMER_PORTAL_CHAT_FUNCTION") ? (
              user?.userId ? (
                <AuthenticatedUserChat
                  hideCustomChat={hideCustomChat}
                  setHideCustomChat={setHideCustomChat}
                  connectingToSF={connectingToSF}
                  setConnectingToSF={setConnectingToSF}
                  openChatWindow={openChatWindow}
                  setOpenChatWindow={setOpenChatWindow}
                  policies={policies}
                />
              ) : (
                <UnauthenticatedUserChat
                  setHideCustomChat={setHideCustomChat}
                  openChatWindow={openChatWindow}
                  setOpenChatWindow={setOpenChatWindow}
                  connectingToSF={connectingToSF}
                  setConnectingToSF={setConnectingToSF}
                />
              )
            ) : (
              <></>
            )}

            <Routes>
              <Route
                path="/"
                element={
                  <Landing
                    portal="customer"
                    hideCustomChat={hideCustomChat}
                    setHideCustomChat={setHideCustomChat}
                    connectingToSF={connectingToSF}
                    setConnectingToSF={setConnectingToSF}
                    openChatWindow={openChatWindow}
                    setOpenChatWindow={setOpenChatWindow}
                    bannerMessage={bannerMessage}
                    bannerTitle={bannerTitle}
                  />
                }
              />
              <Route path="/register" element={<Register />} />
              <Route
                path="/forgot-password"
                element={<ForgotPasswordContainer />}
              />
              <Route
                path="/reset-password"
                element={<ResetPasswordContainer />}
              />
              <Route path="/forgot-username" element={<ForgotUsername />} />
              {/* unauthorized routes */}
              <Route path="/guest/make-a-payment" element={<UnauthPayment />} />
              {/* <Route path="/guest/claims" element={<UnauthClaims />} /> */}
              {/* <Route path="/guest/claims/:id" element={<ClaimsStatus />} /> */}
              <Route
                path="/guest/claims/file-a-claim"
                element={<UnauthFileAClaim />}
              />
              <Route path="/guest/help" element={<Help />} />
              <Route path="/guest/help/help-faqs" element={<HelpFaqs />} />

              {/* customer profile routes */}
              <Route path="/profile" element={<Profile />} />

              <Route
                path="/csradmin"
                element={isCSRAdmin(csrUser) ? <CSRAdmin /> : <AccessDenied />}
              />
              <Route
                path="/csr/forms"
                element={
                  isCSR(csrUser) || isCSRAdmin(csrUser) ? (
                    <CSRFormsPanel />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              <Route
                path="/payee"
                element={
                  isFlagEnabled("ENABLE_PAYEES_AS_OWNERS") ? (
                    <Payee />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              {/* policy routes */}
              <Route
                path="/policies"
                element={
                  useAuthorizationCheck(1) ? (
                    <Policies />
                  ) : csrUser?.userId && !user?.userId ? (
                    <div />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              <Route
                path="/policies/:policy_id"
                element={
                  useAuthorizationCheck(2) ? (
                    <PolicyDetails />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              {/* payment routes */}
              <Route
                path="/payment"
                element={
                  useAuthorizationCheck(3) ? <Payment /> : <AccessDenied />
                }
              />
              <Route
                path="/payment/edit-payment-frequency/:policy_id"
                element={
                  useAuthorizationCheck(3) &&
                  isFlagEnabled("ENABLE_RECURRING_PAYMENTS") ? (
                    <EditPaymentFrequency />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              <Route
                path="/payment/autopay/:policy_id"
                element={
                  useAuthorizationCheck(3) ? <AutoPay /> : <AccessDenied />
                }
              />
              <Route
                path="/payment/manageautopay/:policy_id"
                element={
                  useAuthorizationCheck(3) &&
                  isFlagEnabled("ENABLE_RECURRING_PAYMENTS") ? (
                    <ManageAutoPay />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              <Route
                path="/payment/make-a-payment"
                element={
                  useAuthorizationCheck(3) ? <MakeAPayment /> : <AccessDenied />
                }
              />
              <Route
                path="/payment/make-a-payment/:policy_id"
                element={
                  useAuthorizationCheck(3) ? <MakeAPayment /> : <AccessDenied />
                }
              />
              <Route
                path="/payment/billing"
                element={
                  useAuthorizationCheck(3) ? <Billing /> : <AccessDenied />
                }
              />
              <Route
                path="/payment/manage-payments"
                element={
                  useAuthorizationCheck(3) ? (
                    <ManagePayment />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              {/* service and claim routes */}

              <Route
                path="/services-claims/services"
                element={
                  useAuthorizationCheck(4) &&
                  isFlagEnabled("ENABLE_SERVICE_TRACKING") ? (
                    <Services setOpenChatWindow={setOpenChatWindow} />
                  ) : (
                    <AccessDenied />
                  )
                }
              />

              <Route
                path="/services-claims"
                element={
                  useAuthorizationCheck(4) &&
                  !isFlagEnabled("ENABLE_SERVICE_TRACKING") ? (
                    <ServicesClaims />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              <Route
                path="/services-claims/services-life-insurance"
                element={
                  useAuthorizationCheck(4) &&
                  !isFlagEnabled("ENABLE_SERVICE_TRACKING") ? (
                    <ServicesLifeInsurance />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              <Route
                path="/services-claims/services-annuity"
                element={
                  useAuthorizationCheck(4) &&
                  !isFlagEnabled("ENABLE_SERVICE_TRACKING") ? (
                    <ServicesAnnuity />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              <Route
                path="/services-claims/forms"
                element={
                  useAuthorizationCheck(4) ? <Forms /> : <AccessDenied />
                }
              />

              <Route
                path="/services-claims/claims"
                element={
                  useAuthorizationCheck(12) ? <Claims /> : <AccessDenied />
                }
              />
              <Route
                path="/services-claims/claims/file-a-claim"
                element={
                  useAuthorizationCheck(12) ? (
                    config.ENABLE_ANNUITIES === "true" ? (
                      <FileAClaimAnnuity />
                    ) : (
                      <FileAClaim />
                    )
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              <Route
                path="/services-claims/claims/:id"
                element={
                  useAuthorizationCheck(4) ? (
                    <ClaimsStatus policies={policies} claims={claims} />
                  ) : (
                    <AccessDenied />
                  )
                }
              />
              {/* message routes */}
              <Route
                path="/messages"
                element={
                  useAuthorizationCheck(5) ? <Messages /> : <AccessDenied />
                }
              />
              {/* help routes */}
              <Route path="/help" element={<Help />} />
              <Route path="/help/help-faqs" element={<HelpFaqs />} />

              <Route path="/access-denied" element={<AccessDenied />} />

              <Route path="/terms-of-use" element={<TermsOfUse />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route
                path="/california-consumer-privacy-statement"
                element={<CaliforniaConsumerPrivacyStatement />}
              />
            </Routes>
          </div>
        </main>
        <Footer portal="customer" />
      </div>
      {/* user session */}
      {(user?.userId || csrUser?.userId) && !isLoading && (
        <>
          <Countdown
            date={currentCounterTime}
            key={countdownIdentifier}
            onComplete={() => setSessionDialogState(true)}
            renderer={() => false}
          />
          <SessionRefresherModal
            modalState={sessionDialogState}
            setModalState={setSessionDialogState}
            extendSession={extendSession}
          />
        </>
      )}

      <GeneralPurposeDialog
        state={tokenErrorDialog}
        setState={setTokenErrorDialog}
        title="Session expired"
        showCloseButton={false}
        desc={
          user?.userId
            ? "Your session has expired, please click button below to login to the portal again"
            : "Your session has expired, please click button below to refresh the session and  refresh the page if needed."
        }
        dialogOnClose={() => null}
        enablePrimaryButton={true}
        primaryButtonText={user?.userId ? "Back to log in" : "Refresh session"}
        primaryButtonAction={() => {
          setTokenErrorDialog(false);
          if (user?.userId) {
            navigate("/");
          }
        }}
        enableSecondaryButton={false}
      />
    </div>
  );
}
